import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { TimeZoneData } from 'src/app/modules/registration/data/time-zone.type';
import { RegistrationValidators } from 'src/app/modules/registration/validators/registration.validators';
import { UserRegistrationService } from 'src/app/modules/registration/services/user-registration.service';
import { PatientInfoWithConsent } from 'src/app/modules/registration/services/models/patient-info.model';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { environment } from 'src/environments/environment';
import {
  BaseRestPatientInfoWithProfile,
  RestPatientInfoWithConsents,
} from '../services/models/patient-rest.converter';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { CountryId } from 'src/app/types/country.type';
import { Router } from '@angular/router';
import { AuthService } from '@auth/auth.service';
import { MetadataService } from 'src/app/services/metadata/metadata.service';
import { AnalyticsMode } from 'src/graphql/types/api.types';

@Component({
  selector: 'mpp-extra-consents',
  templateUrl: './consents.component.html',
  styleUrls: ['./consents.component.scss'],
  standalone: false,
})
export class ConsentComponent implements OnInit, OnDestroy {
  @ViewChild('consents') consents: ElementRef;
  selectedCountryId: CountryId;
  readonly nameMaxLength = 50;
  readonly emailMaxLength = 100;

  private unsubscribe$ = new Subject<void>();
  // Reactive form for patient input
  public registrationForm: UntypedFormGroup;
  // checks if user has tried to submit the form
  // if submited form was invalid - focus on invalid fields
  public formSubmitted = false;
  public isValidFormSubmitted = false;
  public isSubmitSuccessful = false;
  public isJapan = false;
  public minimumAge = 13;
  public isEU = environment.isEU;
  public supportedCountryIds: string[] = [];
  public countryNames: string[] = [];
  public countries;
  public timeZoneData: readonly TimeZoneData[];
  public requireAnalyticsMode: boolean;
  public marketingConsentDefault: boolean;
  public requireProcessHealthDataOptIn: boolean;
  public optInAnalyticsMode: AnalyticsMode;
  public optOutAnalyticsMode: AnalyticsMode;
  public parentComponent = 'extraConsentsForm';

  constructor(
    private userRegistrationService: UserRegistrationService,
    private spinnerService: SpinnerService,
    private analyticsService: AnalyticsService,
    private router: Router,
    private authService: AuthService,
    private metadataService: MetadataService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.metadataService.getMetadataFromApi().subscribe((metadata) => {
      this.requireAnalyticsMode = metadata.countries.find(
        (country) => country.code === this.metadataService.getSelectedCountryId(),
      )?.requireAnalyticsMode;

      this.marketingConsentDefault = metadata.countries.find(
        (country) => country.code === this.metadataService.getSelectedCountryId(),
      )?.marketingConsentDefault;

      this.requireProcessHealthDataOptIn = metadata.countries.find(
        (country) => country.code === this.metadataService.getSelectedCountryId(),
      )?.requireProcessHealthDataOptIn;

      this.optInAnalyticsMode = metadata.countries.find(
        (country) => country.code === this.metadataService.getSelectedCountryId(),
      )?.optInAnalyticsMode;

      this.optOutAnalyticsMode = metadata.countries.find(
        (country) => country.code === this.metadataService.getSelectedCountryId(),
      )?.optOutAnalyticsMode;
    });

    this.minimumAge = this.isEU ? 16 : 13;

    this.registrationForm = new UntypedFormGroup({
      patientLegalConsents: new UntypedFormGroup({
        analyticsMode: new UntypedFormControl(false),
        shareDetailsWithProviderOptIn: new UntypedFormControl(false),
        keepMeInformed: new UntypedFormControl(this.marketingConsentDefault),
        processHealthDataOptIn: new UntypedFormControl(false, [
          RegistrationValidators.conditionalValidator(
            () => this.requireProcessHealthDataOptIn,
            Validators.required,
          ),
          RegistrationValidators.conditionalValidator(
            () => this.requireProcessHealthDataOptIn,
            RegistrationValidators.isAcceptedProcessingHealthData,
          ),
        ]),
      }),
    });

    this.analyticsService.logScreenViewWeb('screen_therapy_pap_consents');
  }

  getPatientInRestFormat(): BaseRestPatientInfoWithProfile {
    return new RestPatientInfoWithConsents(
      this.registrationForm,
      this.requireAnalyticsMode,
      this.optInAnalyticsMode,
      this.optOutAnalyticsMode,
    );
  }

  public onSubmit(): void {
    this.formSubmitted = true;
    const patientInRestFormat = this.getPatientInRestFormat();
    if (this.registrationForm.valid) {
      this.isValidFormSubmitted = true;
      this.registerPatientAccount(patientInRestFormat.getPatientInfoForConsents());
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public trackBy(index: number, el: HTMLElement): string {
    return el.id;
  }

  /**
   * Checks if current form control is required
   * @param formControlName name of control which is checked
   */
  public isFormControlRequired(formControlName: string): boolean {
    return (
      (this.registrationForm.get(formControlName).errors?.required &&
        (this.registrationForm.get(formControlName).touched ||
          this.registrationForm.get(formControlName).dirty)) ||
      (this.registrationForm.get(formControlName).invalid && this.formSubmitted)
    );
  }

  private registerPatientAccount(patientInfo: PatientInfoWithConsent): void {
    const spinner = this.spinnerService.show();
    this.userRegistrationService.registerPatientWithConsent(patientInfo).subscribe((response) => {
      spinner.hide();

      if (response[1]) {
        this.router.navigate(['/dashboard']);
      }
    });
  }

  async onCancel(): Promise<void> {
    const spinner = this.spinnerService.show();
    this.analyticsService.logLoggedOutEvent();
    await this.authService.logout();
    spinner.hide();
  }
}
